import { combine, persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

export const saveKey = 'trade-setting';

// store
export const useTradeSettingStore = createWithEqualityFn(
  persist(
    combine(
      {
        // slippageAuto true → min-receive uses the price-impact auto-buffer;
        // false → uses `slippage` (manual max-slippage %, APMM-style).
        slippage: 3,
        slippageAuto: true,
        deadline: 30,
      },
      set => {
        return {
          setSlippage(slippage: number) {
            set({ slippage });
          },
          setSlippageAuto(slippageAuto: boolean) {
            set({ slippageAuto });
          },
          setDeadline(deadline: number) {
            set({ deadline });
          },
        };
      }
    ),
    { name: saveKey }
  ),
  shallow
);

export type TradeType = 'buy' | 'sell';
export type OptionSide = 'yes' | 'no';
export type TradeMode = 'market' | 'limit';

export type TradeParams = {
  type: TradeType;
  marketAddress: string;
  amount?: bigint;
  optionAddress?: string;
  optionSide?: OptionSide; // For multi-option markets: which side (yes/no) to buy
  // CLOB order mode and limit order inputs. Optional because most call sites
  // (badges, deep links, history rows) only care about buy/sell + option.
  tradeMode?: TradeMode;
  limitPrice?: string; // USD decimal string in [$0.01, $0.99], e.g. "0.65" (priceBps 6500)
  deadlineMinutes?: number; // 0 = GTC (good-till-cancelled)
};

// store
export const useTradeParamsStore = createWithEqualityFn(
  combine(
    {
      type: 'buy' as TradeType,
      marketAddress: '',
      uid: undefined,
      optionAddress: undefined,
      optionSide: undefined,
      amount: undefined,
      tradeMode: 'market' as TradeMode,
      limitPrice: '',
      deadlineMinutes: 60,
    } as TradeParams,
    set => ({
      setType: (type?: TradeType) => set({ type }),
      setMarketAddress: (marketAddress: string) => set({ marketAddress }),
      setOptionAddress: (optionAddress: string) => set({ optionAddress }),
      setOptionSide: (optionSide?: OptionSide) => set({ optionSide }),
      setAmount: (amount?: bigint) => set({ amount }),
      setTradeMode: (tradeMode: TradeMode) => set({ tradeMode }),
      setLimitPrice: (limitPrice: string) => set({ limitPrice }),
      setDeadlineMinutes: (deadlineMinutes: number) => set({ deadlineMinutes }),
      setTradeParams: (tradeParams: Partial<TradeParams> & { type?: TradeType }) =>
        set(state => ({ ...state, ...tradeParams })),
      margeTradeParams: (tradeParams: Partial<TradeParams>) => set(state => ({ ...state, ...tradeParams })),
    })
  ),
  shallow
);
